import React, { useState, useEffect, useCallback, useMemo } from "react";
import { colors } from "../../utils/colors";
import { getToken } from "../../Api/auth";
import { API_URLS } from "../../Api/urls";
import type { ActiveTab, Teacher } from "../../utils/types";
import BottomNav from "../../components/BottomNav";
import TeacherAttendanceDetail from "../all-teacher-attendance-supervisor/TeacherAttendanceDetail";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TeacherListItem {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  gender: "male" | "female" | string;
  is_active: boolean;
  expertise: string;
  other_expertise: string | null;
}

interface AllTeacherAttendanceSummary {
  date: string;
  total: number;
  present: number;
  absent: number;
  late: number;
  on_time: number;
  not_marked: number;
  teachers: {
    teacher_id: number;
    status: "on_time" | "absent" | "late";
    created_at?: string;
  }[];
}

interface Props {
  onNavigate: (tab: ActiveTab) => void;
  teacher: Teacher;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayISO = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const formatTime = (isoDatetime: string) => {
  const normalized = isoDatetime.replace(" ", "T").trim();
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
  const utcString = hasTimezone ? normalized : normalized + "Z";
  const d = new Date(utcString);
  return d
    .toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
};

const STATUS_META = {
  present: {
    label: "Present",
    color: "#1e40af",
    bg: "#eff6ff",
    dot: "#3b82f6",
    border: "#93c5fd",
  },
  on_time: {
    label: "On Time",
    color: "#166534",
    bg: "#f0fdf4",
    dot: "#22c55e",
    border: "#86efac",
  },
  absent: {
    label: "On Leave",
    color: "#92400e",
    bg: "#fff7ed",
    dot: "#f59e0b",
    border: "#fcd34d",
  },
  late: {
    label: "Late",
    color: "#854d0e",
    bg: "#fef9c3",
    dot: "#eab308",
    border: "#facc15",
  },
  not_marked: {
    label: "Not Marked",
    color: "#9ca3af",
    bg: "#f9fafb",
    dot: "#d1d5db",
    border: "#e5e7eb",
  },
} as const;

const AVATAR_COLORS = [
  "#7C3AED",
  "#0369a1",
  "#0f766e",
  "#b45309",
  "#be185d",
  "#1d4ed8",
  "#065f46",
];

const TITLE_RE = /^(mr\.?|mrs\.?|ms\.?|dr\.?|prof\.?)\s+/i;
function stripTitle(name: string) {
  return name.replace(TITLE_RE, "").trim();
}

function avatarColor(id: number) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

// ─── Shimmer ──────────────────────────────────────────────────────────────────

const shimmerStyle: React.CSSProperties = {
  background: "linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 50%,#e5e7eb 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s infinite",
  borderRadius: 8,
  display: "inline-block",
};

// ─── Component ────────────────────────────────────────────────────────────────

const AllTeacherAttendance: React.FC<Props> = ({ onNavigate, teacher }) => {
  const [search, setSearch] = useState("");
  const [teachers, setTeachers] = useState<TeacherListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [dayStatus, setDayStatus] = useState<
    Record<number, "on_time" | "absent" | "late">
  >({});
  const [dayMarkedAt, setDayMarkedAt] = useState<Record<number, string>>({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summary, setSummary] = useState<Omit<
    AllTeacherAttendanceSummary,
    "teachers"
  > | null>(null);
  const [selectedTeacher, setSelectedTeacher] =
    useState<TeacherListItem | null>(null);

  // ── Fetch all teachers once ────────────────────────────────────────────────
  const fetchTeachers = useCallback(async () => {
    setListLoading(true);
    try {
      const token = getToken();
      const base = API_URLS.GET_TEACHER_LIST.replace(/[?&]search=[^&]*/g, "");
      const res = await fetch(base, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: TeacherListItem[] = await res.json();
      const list: TeacherListItem[] = Array.isArray(data)
        ? data.map((t) => ({ ...t, full_name: stripTitle(t.full_name) }))
        : [];

      // If the logged-in supervisor is not in the teacher list (API excludes
      // supervisor-role accounts), inject them so their attendance row shows.
      const supervisorId = Number(teacher.id);
      if (!list.some((t) => t.id === supervisorId)) {
        list.unshift({
          id: supervisorId,
          full_name: stripTitle(teacher.name),
          email: teacher.email ?? "",
          phone: teacher.phone ?? "",
          gender: teacher.gender ?? "",
          is_active: true,
          expertise: teacher.expertise ?? teacher.subject ?? "",
          other_expertise: null,
        });
      }

      setTeachers(list);
    } catch (err) {
      console.error("Teacher list error:", err);
      setTeachers([]);
    } finally {
      setListLoading(false);
    }
  }, [teacher]);

  // ── Fetch today's attendance summary ──────────────────────────────────────
  const fetchDaySummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const token = getToken();
      const res = await fetch(API_URLS.GET_ALL_TEACHER_ATTENDANCE(todayISO()), {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AllTeacherAttendanceSummary = await res.json();
      setSummary({
        date: data.date,
        total: data.total,
        present: data.present,
        absent: data.absent,
        late: data.late,
        on_time: data.on_time,
        not_marked: data.not_marked,
      });
      const map: Record<number, "on_time" | "absent" | "late"> = {};
      const timeMap: Record<number, string> = {};
      (data.teachers ?? []).forEach((t) => {
        // Legacy API may still return "present" - normalise to "on_time"
        const normalised = ((t.status as string) === "present" ? "on_time" : t.status) as
          | "on_time"
          | "absent"
          | "late";
        if (normalised === "on_time" || normalised === "absent" || normalised === "late") {
          map[t.teacher_id] = normalised;
        }
        if (t.created_at) timeMap[t.teacher_id] = t.created_at;
      });
      setDayStatus(map);
      setDayMarkedAt(timeMap);
    } catch (err) {
      console.error("Day summary error:", err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchTeachers();
    fetchDaySummary();
  }, [fetchTeachers, fetchDaySummary]);

  // ── Local search filter ────────────────────────────────────────────────────
  const filteredTeachers = useMemo(() => {
    if (!search.trim()) return teachers;
    const q = search.toLowerCase();
    return teachers.filter(
      (t) =>
        t.full_name.toLowerCase().includes(q) ||
        t.expertise.toLowerCase().includes(q),
    );
  }, [teachers, search]);

  // ── Group teachers by status (Not Marked → Absent → Late → On Time) ───────
  const groupedTeachers = useMemo(() => {
    const groups: Record<
      "not_marked" | "absent" | "late" | "on_time",
      TeacherListItem[]
    > = { not_marked: [], absent: [], late: [], on_time: [] };
    filteredTeachers.forEach((t) => {
      const status = dayStatus[t.id];
      const bucket = status && groups[status] ? groups[status] : groups.not_marked;
      bucket.push(t);
    });
    return groups;
  }, [filteredTeachers, dayStatus]);

  const SECTION_ORDER: Array<{
    key: "not_marked" | "absent" | "late" | "on_time";
    title: string;
  }> = [
    { key: "not_marked", title: "Not Marked" },
    { key: "absent", title: "On Leave" },
    { key: "late", title: "Late" },
    { key: "on_time", title: "On Time" },
  ];

  // ── If a teacher is selected → show detail page ───────────────────────────
  if (selectedTeacher) {
    return (
      <TeacherAttendanceDetail
        teacher={selectedTeacher}
        onBack={() => {
          setSelectedTeacher(null);
          fetchDaySummary(); // refresh badges on return
        }}
      />
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        height: "100dvh",
        background: "#FDFCFE",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`
        @keyframes shimmer {
          0%   { background-position:  200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes slideUp {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0);    }
        }
        .teacher-row { transition: background 0.12s; }
        .teacher-row:active { background: #f3f4f6 !important; }
      `}</style>

      {/* ── Header ── */}
      <div
        style={{
          padding: "36px 20px 16px",
          borderBottom: `1px solid ${colors.borderLight}`,
          flexShrink: 0,
        }}
      >
        {/* Title row */}
        <div
          style={{
            display: "flex row",
            alignItems: "center",
            justifyContent: "start",
            marginBottom: 14,
          }}
        >
          {/* LEFT: Back + Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Back Button */}
            <button
              onClick={() => onNavigate("home")} // 👈 navigate to home
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={colors.text}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>

            {/* Title + Date */}
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: colors.text,
                  lineHeight: 1.2,
                }}
              >
                Teacher Attendance
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.textMuted,
                  marginTop: 3,
                  letterSpacing: "0.4px",
                }}
              >
                {new Date().toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  timeZone: "Asia/Kolkata",
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Summary chips (unchanged) */}
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {summaryLoading
              ? [56, 56, 44, 44].map((w, i) => (
                  <div
                    key={i}
                    style={{ ...shimmerStyle, width: w, height: 26 }}
                  />
                ))
              : summary &&
                (["present", "on_time", "absent", "late"] as const).map((key) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: STATUS_META[key].bg,
                      border: `1px solid ${STATUS_META[key].border}`,
                      borderRadius: 20,
                      padding: "4px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: STATUS_META[key].color,
                    }}
                  >
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: STATUS_META[key].dot,
                      }}
                    />
                    {summary[key]}
                  </div>
                ))}
          </div>
        </div>

        {/* Search bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "#F3F4F6",
            borderRadius: 12,
            padding: "10px 14px",
            border: `1.5px solid ${colors.borderLight}`,
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke={colors.textMuted}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teachers…"
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              fontSize: 15,
              color: colors.text,
              outline: "none",
              fontFamily: "inherit",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={colors.textMuted}
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── List body ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: 80,
        }}
      >
        {listLoading ? (
          // Skeleton rows
          <div
            style={{
              padding: "10px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: "#fff",
                  borderRadius: 16,
                  border: `1.5px solid ${colors.borderLight}`,
                  padding: "14px 16px",
                  animation: `slideUp 0.25s ease ${(i * 0.04).toFixed(2)}s both`,
                }}
              >
                <div
                  style={{
                    ...shimmerStyle,
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                  }}
                >
                  <div style={{ ...shimmerStyle, width: "55%", height: 14 }} />
                  <div style={{ ...shimmerStyle, width: "38%", height: 11 }} />
                </div>
                <div
                  style={{
                    ...shimmerStyle,
                    width: 70,
                    height: 24,
                    borderRadius: 20,
                  }}
                />
              </div>
            ))}
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              color: colors.textMuted,
              gap: 10,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.border}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              No teachers found
            </div>
            {search && (
              <div style={{ fontSize: 13, color: colors.textLight }}>
                Try a different search term
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: "10px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            {SECTION_ORDER.map(({ key, title }) => {
              const list = groupedTeachers[key];
              if (!list.length) return null;
              const meta = STATUS_META[key];

              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {/* Section title */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "2px 4px",
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: meta.dot,
                      }}
                    />
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: colors.text,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: meta.color,
                        background: meta.bg,
                        border: `1px solid ${meta.border}`,
                        borderRadius: 20,
                        padding: "2px 8px",
                      }}
                    >
                      {list.length}
                    </div>
                  </div>

                  {/* Section rows */}
                  {list.map((teacher, idx) => {
                    const isNotMarked = key === "not_marked";
                    return (
                    <button
                      key={teacher.id}
                      className="teacher-row"
                      onClick={() => setSelectedTeacher(teacher)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        background: isNotMarked ? "#fff" : meta.bg,
                        border: `1.5px solid ${isNotMarked ? colors.borderLight : meta.border}`,
                        borderRadius: 16,
                        padding: "14px 16px",
                        cursor: "pointer",
                        textAlign: "left",
                        animation: `slideUp 0.25s ease ${(idx * 0.04).toFixed(2)}s both`,
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          background: isNotMarked ? avatarColor(teacher.id) : meta.dot,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 15,
                          fontWeight: 800,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(teacher.full_name)}
                      </div>

                      {/* Name + subject + status */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: colors.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {teacher.full_name}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: colors.textMuted,
                            fontWeight: 500,
                            marginTop: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          ID: {teacher.id}
                        </div>
                        {/* Status badge - third row */}
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            background: meta.bg,
                            border: `1px solid ${meta.border}`,
                            borderRadius: 20,
                            padding: "4px 10px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: meta.color,
                            marginTop: 6,
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: meta.dot,
                            }}
                          />
                          {meta.label}
                        </div>
                      </div>

                      {/* Marked-at time */}
                      {!isNotMarked && dayMarkedAt[teacher.id] && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: meta.color,
                            background: "#fff",
                            border: `1px solid ${meta.border}`,
                            borderRadius: 8,
                            padding: "4px 8px",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {formatTime(dayMarkedAt[teacher.id])}
                        </div>
                      )}

                      {/* Chevron */}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={colors.textLight}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bottom nav ── */}
      <BottomNav activeTab="all-teacher-attendance" onNavigate={onNavigate} />
    </div>
  );
};

export default AllTeacherAttendance;
